import {Injectable} from '@angular/core';
import {TranslateService} from '@ngx-translate/core';
import {asyncScheduler, BehaviorSubject} from 'rxjs';
import {ConfiguracoesService} from '~core/api/configuracoes.service';
import {EntityConfiguracao, entityConfigVoid} from '~ngrx/entitys/entity-configuracao/entity-configuracao.model';
import {environment} from '~env/environment';
import {Store} from '@ngrx/store';
import {getAuth} from '~feature/feature-auth/feature-auth.selectors';
import {filter, observeOn} from 'rxjs/operators';
import {setLogo, setPalette} from '~feature/feature-app/feature-app.actions';

@Injectable({providedIn: 'root'})
export class ConfigService {

    private languages = this.translate.getLangs();
    private fallbackLanguage = environment.adds.defaultLanguage;
    private readonly langs = [environment.adds.defaultLanguage, 'en-US'];
    private langConfig: string | undefined;

    langChangeObserver: BehaviorSubject<undefined> = new BehaviorSubject<undefined>(undefined);

    constructor(
        private configuracoesService: ConfiguracoesService,
        private translate: TranslateService,
        private store: Store
    ) {
    }

    setUp(): void {
        this.translate.addLangs(this.langs);
        this.translate.setDefaultLang(this.fallbackLanguage);

        this.store.select(getAuth).pipe(
            filter(a => !!a.token),
            observeOn(asyncScheduler)
        ).subscribe(_ => this.loadConfigs());
    }

    private loadConfigs(): void {
        this.configuracoesService.getConfiguracoes().subscribe((config: EntityConfiguracao) => {
            if (!config) {
                config = entityConfigVoid();
            }
            this.store.dispatch(setPalette({palette: config.tema ?? ''}));
            this.store.dispatch(setLogo({logo: config.parametros.linkLogo ?? ''}));
            if (this.langConfig !== config.idioma) {
                this.langConfig = config.idioma ?? '';

                const targetLangCountry = this.langConfig || this.fallbackLanguage; // this.translate.getBrowserCultureLang();
                const targetLang = this.fallbackLanguage; // this.translate.getBrowserLang();

                let perfectMatch = false;
                let partialMatch = '';

                for (const lang of this.languages) {
                    if (lang === targetLangCountry) {
                        perfectMatch = true;
                        break;
                    }
                    if (!partialMatch && lang.split('-')[0] === targetLang) {
                        partialMatch = lang;
                    }
                }
                if (perfectMatch) {
                    this.translate.use(targetLangCountry);
                } else if (partialMatch) {
                    this.translate.use(partialMatch);
                } else {
                    this.translate.use(this.fallbackLanguage);
                }

                this.langChangeObserver.next(undefined);
            } else {
                this.langConfig = undefined;
            }
        });
    }

    changeLanguage(lang: string): void {
        if (this.languages.includes(lang)
        ) {
            this.translate.use(lang);
        } else {
            this.translate.use(this.fallbackLanguage);
        }
        this.langChangeObserver.next(undefined);
    }

    getLangChangeObserver(): BehaviorSubject<undefined> {
        return this.langChangeObserver;
    }
}
